import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import CardContent from '@mui/material/CardContent';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Link from '@mui/material/Link';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import parse from 'html-react-parser';

import {
  getResultStatus,
  getLabel,
  getDescription,
  getTranslatedText
} from '../pages/utils_v2/kumuHelper';
import { QuestionPhase, ResultType } from '../constants';

export default function LegislationCard({ legislation, setCurrentLegislation, setQuestionPhase, results }) {

  const { t, i18n } = useTranslation('translation');
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [cardStatus, setCardStatus] = useState(null);
  const [cardIcon, setCardIcon] = useState(<Icon icon="carbon:checkbox-indeterminate-filled" width="24" height="24" />);
  const [iconColor, setIconColor] = useState('info');
  const [isExpanded, setIsExpanded] = useState(false);

  const handleStart = () => {
    setCurrentLegislation(legislation);
    setQuestionPhase(QuestionPhase.SUBQUESTIONS);
  };

  const descriptionRef = useRef(null);

  const toggleExpanded = () => {
    if (isExpanded) {
      setIsExpanded(false);
      setTimeout(() => {
        descriptionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 0);
    } else {
      setIsExpanded(true);
    }
  };

  useEffect(() => {
    const status = getResultStatus(results, legislation.value.attributes.label);
    switch (status) {
      case ResultType.REQUIRED:
      case ResultType.LEARN:
      case ResultType.NOTREQUIRED:
        setCardIcon(<Icon width={32} height={32} icon='carbon:checkmark-filled' />);
        setIconColor('success');
        setCardStatus('REQUIRED');
        break;
      default:
        setCardStatus(null);
        setCardIcon(<Icon icon="carbon:checkbox-indeterminate-filled" width="24" height="24" />);
        setIconColor('info');
    }
  }, [results, legislation.value.attributes.label]);

  const statusStyles = {
    REQUIRED: { backgroundColor: '#e8f5e9', border: '2px solid #4caf50' },
    LEARN: { backgroundColor: '#f0f0f0', border: '2px solid #6a706b' },
    NOTREQUIRED: { backgroundColor: '#ffebee', border: '2px solid #d32f2f' },
    DEFAULT: { backgroundColor: '#fff', border: '1px solid #ccc' },
  };

  const getGradientColor = () => {
    switch (cardStatus) {
      case 'REQUIRED': return 'rgba(232,245,233,1)';
      case 'LEARN': return 'rgba(240,240,240,1)';
      case 'NOTREQUIRED': return 'rgba(255,235,238,1)';
      default: return 'rgba(255,255,255,1)';
    }
  };

  const fullDescriptionHTML = getDescription(legislation, i18n.language) || '';

  const stripHTML = (html) => {
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };

  const plainText = stripHTML(fullDescriptionHTML);
  const charLimit = 200;
  const shouldTruncate = plainText.length > charLimit;


  return (
    <Card
      sx={{
        position: 'relative',
        maxWidth: '100%',
        // No fixed minHeight on mobile — let content dictate height
        minHeight: isMobile ? 'unset' : 200,
        borderRadius: '15px',
        ':hover': { boxShadow: 2 },
        ...(statusStyles[cardStatus] || statusStyles.DEFAULT),
      }}
    >
      <CardContent sx={{ p: isMobile ? 1 : 2 }}>

        {/* Status badge — move below title on mobile so it doesn't overlap */}
        {cardStatus && (
          <Box sx={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            zIndex: 1,
          }}>
            <IconButton
              color={iconColor}
              aria-label="card-status"
              disableRipple
              sx={{ p: 0.5 }}
            >
              {cardIcon}&nbsp;
              <span style={{ fontWeight: 'bold', fontSize: isMobile ? 11 : 12 }}>
                {cardStatus === 'REQUIRED' && t('Completed')}
                {cardStatus === 'LEARN' && t('Learn More')}
                {cardStatus === 'NOTREQUIRED' && t('Not Required')}
              </span>
            </IconButton>
          </Box>
        )}

        <Box sx={{ padding: isMobile ? 1 : 2 }} ref={descriptionRef}>
          <Stack direction='column' className='alert alert-info'>

            {/* Title */}
            <h3
              id={legislation.value.attributes.label}
              style={{
                margin: '0 0 8px 0',
                fontSize: isMobile ? '18px' : '20px',
                lineHeight: 1.4,
                // On desktop leave room for the absolute status badge
                paddingRight: !isMobile && cardStatus ? '120px' : 0,
              }}
            >
              <i>{getLabel(legislation, i18n.language)}</i>
            </h3>

            {/* Description */}
            <Box>
              <div style={{
                maxHeight: isExpanded ? 'none' : '150px',
                overflow: 'hidden',
                position: 'relative',
                fontSize: isMobile ? '16px' : '16px',
                lineHeight: 1.6,
              }}>
                {fullDescriptionHTML ? parse(fullDescriptionHTML) : null}
                {shouldTruncate && !isExpanded && (
                  <div style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '50px',
                    background: `linear-gradient(to bottom, transparent, ${getGradientColor()})`,
                    pointerEvents: 'none',
                  }} />
                )}
              </div>
              {shouldTruncate && (
                <Box sx={{ textAlign: 'left', mt: 1 }}>
                  <Link
                    component="button"
                    variant="body1"
                    onClick={toggleExpanded}
                    sx={{
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      color: 'primary.main',
                      fontSize: isMobile ? '14px' : '16px',
                      fontWeight: 'bold',
                    }}
                  >
                    {isExpanded ? t('Show less') : t('Show more')}
                  </Link>
                </Box>
              )}
            </Box>

            {/* Action buttons — stack vertically on mobile */}
            <Box
              mt={2}
              sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                gap: isMobile ? 1 : 0,
                alignItems: isMobile ? 'stretch' : 'center',
              }}
            >
              <button
                className="btn btn-primary btn-sm"
                style={{
                  width: isMobile ? '100%' : 'auto',
                  minHeight: '40px',
                  marginRight: isMobile ? 0 : '8px',
                }}
                onClick={handleStart}
              >
                {t('Start Questions')}
              </button>
              
              <a className="btn btn-primary btn-sm"
                style={{
                  width: isMobile ? '100%' : 'auto',
                  minHeight: '40px',
                  textAlign: 'center',
                }}
                href={`/${i18n.language}/overview/${legislation.value.attributes['reference id']}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('Learn More')}
              </a>
            </Box>

          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
}